import type { ApplicationConfig as MobileAppConfig } from '@code-troopers/mobile-preview/types/grab/types'
import axios from 'axios'

import { type TranslatableText } from '../../../types/TranslatableText.type'
import { type FeatureName } from '../../pages/configDev/features'
import { cloneDeep } from '../../utils'
import { type SocialLinks } from './festival.service'
import { type Font } from './fontsService'
import { colorService } from './utils.service'

export type AdImageLink = {
  name: string
  percentage: number
  description?: TranslatableText | null
  image?: string | null
  link?: string | null
  videoFilename?: string | null
  startDate?: string | null
  endDate?: string | null
}

export type ApplicationFeatures = {
  [k in FeatureName]: boolean
}

type ApplicationSpotify = {
  clientId?: string | null
  clientSecret?: string | null
  redirectURI?: string | null
  valid: boolean
}

export type TwilioConfig = {
  twilioAccountSid?: string
  twilioApiKeySid?: string
  twilioApiKeySecret?: string
  twilioServiceSid?: string
  twilioAuthToken?: string
  twilioPushCredentialSid?: string
}

export type ApplicationConfig = {
  _id: string
  colorBackground: string
  colorOverrideNavbarIcon?: string | undefined
  colorOverrideBackground?: string | undefined
  colorOverrideHeaderAccent?: string | undefined

  colorTitle: string
  colorOverrideNavbarBackground?: string | undefined
  colorOverrideHeaderBackground?: string | undefined
  colorOverrideNavbarTextUnselected?: string | undefined
  colorOverrideTitle?: string | undefined
  colorOverrideTimetableHours?: string | undefined

  colorAction: string
  colorOverrideBlocIcons?: string | undefined
  colorOverrideCountdown?: string | undefined

  colorBlockBackground: string
  colorOverrideBlockBackground?: string | undefined
  colorOverrideNavbarBlock?: string | undefined

  colorBlockText: string
  colorOverrideBlockText?: string | undefined
  colorOverrideNavbarTextSelected?: string | undefined

  menuBackgroundImage: string
  fontTitle?: Font | null
  fontTextRegular?: Font | null
  fontTextSemiBold?: Font | null
  fontTextBold?: Font | null
  fontTextRegularItalic?: Font | null
  fontTextBoldItalic?: Font | null
  fbNotifTopic: string
  timezone: string
  drawerAds: AdImageLink[]
  splashAds: AdImageLink[]
  splashImage: string | null
  splashDuration: number
  deeplinkHost: string
  deeplinkPathPrefix: string
  // App Links Configuration
  weezeventWidgetUrl?: string
  androidPackageName?: string
  androidSha256CertFingerprints?: string[]
  iosAppIds?: string[]
  // Egaluantes synchronisation: recipients of the refused-cycle alerts
  egaluantesAlertEmails?: string[]

  features: ApplicationFeatures
  spotify: ApplicationSpotify
  editionVersion?: string
  showAlertVersion?: boolean
  showNotificationOnboarding?: boolean
  showBlockShadows?: boolean
  enableVibrations?: boolean
  matomoIdSite?: number
  isDirty?: boolean
  remotionUrl?: string | null
  socialIconStyle?: MobileAppConfig['socialIconStyle']
  socialIconColor?: MobileAppConfig['socialIconColor']
  // raw file ids, keyed by social network
  customSocialIcons?: Partial<Record<SocialLinks, string>>
} & TwilioConfig

export function createDefaultApplicationConfig(): ApplicationConfig {
  return {
    _id: '',
    colorBackground: '',
    colorTitle: '',
    colorAction: '',
    colorBlockBackground: '',
    colorBlockText: '',
    menuBackgroundImage: '',
    fbNotifTopic: '',
    timezone: '',
    drawerAds: [],
    splashAds: [],
    splashImage: null,
    splashDuration: 0,
    deeplinkHost: '',
    deeplinkPathPrefix: '',
    features: {} as ApplicationFeatures,
    spotify: { valid: false },
  }
}

type ColorKeysObj = {
  [k in keyof ApplicationConfig]: k extends `color${string}` ? k : never
}
export type ApplicationConfigColorKeys = Exclude<
  ColorKeysObj[keyof ColorKeysObj],
  undefined
>

type FontKeysObj = {
  [k in keyof ApplicationConfig]: k extends `font${string}` ? k : never
}
export type ApplicationConfigFontKeys = Exclude<
  FontKeysObj[keyof FontKeysObj],
  undefined
>

function toJsFormat(cfg: ApplicationConfig): ApplicationConfig {
  function convert(key: ApplicationConfigColorKeys): void {
    cfg[key] = cfg[key] != null ? colorService.argb_to_rgba(cfg[key]) : ''
  }

  convert('colorBackground')
  convert('colorTitle')
  convert('colorBlockText')
  convert('colorAction')
  convert('colorBlockBackground')

  return cfg
}

function applicationCfgToDbFormat(
  config: ApplicationConfig,
): ApplicationConfig {
  const cfg = cloneDeep(config)

  function convert(key: ApplicationConfigColorKeys): void {
    cfg[key] = cfg[key] != null ? colorService.rgba_to_argb(cfg[key]) : ''
  }

  convert('colorBackground')
  convert('colorTitle')
  convert('colorBlockText')
  convert('colorAction')
  convert('colorBlockBackground')

  return cfg
}

export const ApplicationConfigService = {
  async get(): Promise<ApplicationConfig> {
    const { data } = await axios.get<ApplicationConfig>(
      '/api/applicationConfig',
    )
    return toJsFormat(data)
  },
  async updateApplicationCfg(
    applicationConfig: ApplicationConfig,
  ): Promise<ApplicationConfig> {
    const { data } = await axios.put<ApplicationConfig>(
      '/api/applicationConfig',
      applicationCfgToDbFormat(applicationConfig),
    )
    return toJsFormat(data)
  },

  async updateSplashImage(image: string): Promise<ApplicationConfig> {
    const { data } = await axios.post<ApplicationConfig>(
      '/api/applicationConfig/updateSplashImage',
      {
        splashImage: image,
      },
    )
    return toJsFormat(data)
  },

  async updateApplicationCfgTwilio(twilioConfig: TwilioConfig): Promise<void> {
    await axios.put('/api/applicationConfig/updateTwilio', twilioConfig)
  },

  async shiftDates(): Promise<void> {
    await axios.post('/api/admin/shift-dates?shift=-2')
  },

  async updateApplicationCfgFeatures(
    applicationConfig: ApplicationConfig,
  ): Promise<ApplicationConfig> {
    const { data } = await axios.put<ApplicationConfig>(
      '/api/applicationConfig/updateFeatures',
      applicationConfig,
    )
    return toJsFormat(data)
  },

  async markDirty(): Promise<ApplicationConfig> {
    const { data } = await axios.post<ApplicationConfig>(
      '/api/applicationConfig/markDirty',
    )
    return toJsFormat(data)
  },
}
